<template>
  <div>
    <v-divider class="d-xl-none"></v-divider>
    <v-simple-table>
      <template v-slot:default>
        <tbody class="black--text">
          <tr v-if="hasHouseholdInsurance">
            <td class="pl-6 font-normal">{{ insurancePremiumTitle }}</td>
            <td class="text-right font-normal pr-6">
              {{ insurancePremiumPrice | formatCurrency }}
            </td>
          </tr>
          <tr v-if="hasHomeEmergency">
            <td class="pl-6">{{ emergencyCoverTitle }}</td>
            <td class="text-right font-weight-bold pr-6">
              {{
                price.Values.HomeEmergencyPremiumExcludingIPT | formatCurrency
              }}
            </td>
          </tr>
          <tr v-if="hasLegalExpenses">
            <td class="pl-6 font-normal">{{ legalExpensesTitle }}</td>
            <td class="text-right font-normal pr-6">
              {{
                price.Values.LegalExpensesPremiumExcludingIPT | formatCurrency
              }}
            </td>
          </tr>
          <tr v-if="hasRentProtection">
            <td class="pl-6 font-normal">Rent protection premium (ex IPT)</td>
            <td class="text-right font-normal pr-6">
              {{
                price.Values.RentProtectionPremiumExcludingIPT | formatCurrency
              }}
            </td>
          </tr>
          <tr v-if="hasAdministrationCharge">
            <td class="pl-6 font-normal">Administration charge</td>
            <td class="text-right font-normal pr-6">
              {{ price.Values.AdministrationCharge | formatCurrency }}
            </td>
          </tr>
          <tr>
            <td class="pl-6 font-normal">Insurance premium tax*</td>
            <td class="text-right font-normal pr-6">
              {{ price.Values.InsurancePremiumTax | formatCurrency }}
            </td>
          </tr>
          <tr v-if="showTotal">
            <td class="pl-6 font-normal">
              Total premium (inc. IPT{{
                hasAdministrationCharge ? " & admin charge" : ""
              }})
            </td>
            <td class="text-right font-normal pr-6">
              {{ price.Values.TotalPremiumIncludingIPT | formatCurrency }}
            </td>
          </tr>
          <tr v-if="hasCredit">
            <td class="pl-6 font-normal">Charge for credit</td>
            <td class="text-right font-normal pr-6">
              {{ premium.CreditDetails.CreditCharge | formatCurrency }}
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>

    <div
      style="background-color: #354048"
      class="white--text pa-2 d-flex justify-space-between px-6 font-18 font-weight-bold"
    >
      <span>
        Total amount payable
        {{ hasPerMonthText ? "per month" : "" }}
      </span>
      <span>
        {{ price.Values.TotalAmountPayable | formatCurrency }}
      </span>
    </div>
    <p class="pt-2 pb-2 px-6">
      *Note: Insurance premium tax is charged at the current rate and may be
      subject to review.
    </p>

    <v-container v-if="hasCredit">
      <div class="pl-6">
        <h5 class="font-weight-bold pr-6 mb-4 font-20">
          Representative example - {{ durationMonths }} monthly payments
        </h5>
        <p class="pr-6">
          As you have selected to pay by monthly instalments, please note the
          representative example shown below.
        </p>
        <p class="pr-6">Credit is subject to status.</p>
      </div>

      <v-simple-table>
        <template v-slot:default>
          <tbody class="black--text">
            <tr>
              <td class="pl-6 font-normal">
                Representative APR
                {{ premium.CreditDetails.Apr | formatPercent }}
              </td>
              <td class="text-right font-normal pr-6">
                {{ price.Values.TotalAmountPayable | formatCurrency }}
              </td>
            </tr>
            <tr>
              <td class="pl-6 font-normal">Cash price / total credit amount</td>
              <td class="text-right font-normal pr-6">
                {{ price.Values.TotalPremiumIncludingIPT | formatCurrency }}
              </td>
            </tr>
            <tr>
              <td class="pl-6 font-normal">Interest charges</td>
              <td class="text-right font-normal pr-6">
                {{ premium.CreditDetails.CreditCharge | formatCurrency }}
              </td>
            </tr>
            <tr>
              <td class="pl-6 font-normal">Fixed interest rate</td>
              <td class="text-right font-normal pr-6">
                {{
                  premium.CreditDetails.FixedInterestRate
                    | formatPercent(percentageFormat)
                }}
              </td>
            </tr>
            <tr class="grey">
              <td class="pl-6 font-normal">Duration of agreement</td>
              <td class="text-right font-normal pr-6">
                {{ durationMonths }} months
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
      <v-divider></v-divider>
      <p class="px-6 mt-3">
        Payable by one payment of
        <strong>{{
          premium.CreditDetails.InitialPayment | formatCurrency
        }}</strong>
        and
        {{ premium.CreditDetails.NumberOfSubsequentPayments }}
        payments of
        <strong>{{
          premium.CreditDetails.SubsequentPayments | formatCurrency
        }}</strong
        >.
      </p>
      <p class="px-6">
        You will pay
        <strong>{{ price.Values.TotalAmountPayable | formatCurrency }}</strong>
        in total if you choose to pay monthly (including the cost of credit),
        which means that you pay
        <strong>{{
          premium.CreditDetails.CreditCharge | formatCurrency
        }}</strong>
        more than if you paid for your policy in full today.
      </p>
      <p class="px-6">
        Credit is provided by Paymentshield Ltd of Southport Business Park,
        Wight Moss Way, Southport, PR8 4HQ
      </p>
    </v-container>
    <h5 class="font-weight-bold px-6 mt-5 font-18">Cancelling your policy</h5>
    <p class="px-6 mb-0 mt-5">
      {{ cancellingYourPolicyDescription }}
    </p>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { QUESTIONS_MODULE } from "@/store/types/module-types";
import { getProductGroup } from "@/common/question-set";
import { LANDLORDS } from "@/constants/questionset.constants";
import { PRODUCT } from "@/constants/common.constants";
import { BREAKDOWN_PERCENTAGE_FORMAT } from "@/constants/currency-format.constants";
import { roundTo2DecimalPlaces } from "@/common/commission";
export default {
  props: {
    breakdownModel: [Array, Object],
    legalExpensesTitle: {
      type: String,
      default: "Legal expenses cover (ex IPT)",
    },
    insurancePremiumTitle: {
      type: String,
      default: "Insurance premium (ex IPT)",
    },
    cancellingYourPolicyDescription: {
      type: String,
      default:
        "You have the right to cancel your policy within the 14 day cooling off period. After the 14 day cooling off period, you will still have the right to cancel your policy, however you will be charged for the cost of your cover on a pro rata daily basis. If you have suffered a claim and you wish to cancel your policy you will be required to pay for your policy in full.",
    },
    showTotalPremiumIPT: {
      type: Boolean,
      default: false,
    },
  },
  components: {},
  methods: {},
  data() {
    return {
      percentageFormat: BREAKDOWN_PERCENTAGE_FORMAT,
    };
  },
  computed: {
    ...mapGetters(QUESTIONS_MODULE, ["qsId", "productId"]),
    price() {
      return this.breakdownModel.price;
    },
    premium() {
      return this.breakdownModel.premium;
    },
    hasCredit() {
      return !!this.premium.CreditDetails;
    },
    durationMonths() {
      return this.premium.CreditDetails
        ? this.premium.CreditDetails.NumberOfSubsequentPayments + 1
        : null;
    },
    showTotal() {
      return (
        this.showTotalPremiumIPT ||
        this.price.Values.TotalPremiumIncludingIPT !==
          this.price.Values.TotalAmountPayable
      );
    },
    hasHomeEmergency() {
      return (
        this.breakdownModel.price.Values.HomeEmergencyPremiumExcludingIPT > 0
      );
    },
    hasLegalExpenses() {
      return (
        this.breakdownModel.price.Values.LegalExpensesPremiumExcludingIPT > 0 &&
        (this.breakdownModel.currentProductId ?? this.productId) !=
          PRODUCT.Tenants_Liability
      );
    },
    insurancePremiumPrice() {
      if (
        (this.breakdownModel.currentProductId ?? this.productId) ==
        PRODUCT.Tenants_Liability
      ) {
        let tenantsLiabilityPremiumExcludingIPT =
          this.price.Values.TenantsLiabilityPremiumExcludingIPT ?? 0;
        let legalExpensesPremiumExcludingIPT =
          this.price.Values.LegalExpensesPremiumExcludingIPT ?? 0;

        return roundTo2DecimalPlaces(
          tenantsLiabilityPremiumExcludingIPT + legalExpensesPremiumExcludingIPT
        );
      } else {
        return this.price.Values.HouseholdInsurancePremiumExcludingIPT;
      }
    },
    hasRentProtection() {
      return (
        this.breakdownModel.price.Values.RentProtectionPremiumExcludingIPT > 0
      );
    },
    hasHouseholdInsurance() {
      const premium =
        this.breakdownModel?.price?.Values
          ?.HouseholdInsurancePremiumExcludingIPT;
      return typeof premium === "number" && premium > 0;
    },
    productGroup() {
      return getProductGroup(this.qsId);
    },
    emergencyCoverTitle() {
      let productGroup = "Home emergency cover";
      switch (this.productGroup) {
        case LANDLORDS:
          productGroup = "Landlord's emergency cover";
          break;
        default:
          break;
      }

      return `${productGroup} (ex IPT)`;
    },
    hasAdministrationCharge() {
      return this.breakdownModel.price.Values.AdministrationCharge > 0;
    },
    hasPerMonthText() {
      return (
        (this.breakdownModel.currentProductId ?? this.productId) ==
        PRODUCT.Tenants_Contents
      );
    },
  },
};
</script>

<style lang="scss" scoped>
tbody {
  tr:hover {
    background-color: transparent !important;
  }
}

.font-normal {
  font-weight: 400 !important;
}

tr:nth-of-type(odd) {
  background-color: #f5f5f5 !important;
}

.v-data-table__wrapper table tbody tr td {
  font-weight: 400 !important;
  font-size: 16px !important;
}
</style>
